import { Button, FestiveButton, HeadingTag, IconRenderer, ModalPortal, ResponsiveImage } from '@/components';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import Close from '@/assets/close.svg';
import { useSelector } from 'react-redux';
import { selectCampaignIcon, selectCampaignId, selectIsCampaignActive } from '@/store/index';

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
};

const normalize = (str) =>
  str
    ?.toLowerCase()
    .replace(/<\/?p\b[^>]*>/gi, '')
    .replace(/\s+/g, '_');

const CornerPopup = ({ data, ...props }) => {
  const [campaignId, campaignActive] = [useSelector(selectCampaignId), useSelector(selectIsCampaignActive)];
  const [open, setOpen] = useState(true);
  const normalizedHeading = normalize(data.heading?.heading);

  const setCookie = (campaignId) => {
    const date = new Date();
    date.setTime(date.getTime() + 24 * 60 * 60 * 1000);

    document.cookie = `c_popup_closed_${normalizedHeading}=${campaignId};expires=${date.toUTCString()};path=/`;
  };

  useEffect(() => {
    const popupCookie = getCookie(`c_popup_closed_${normalizedHeading}`);

    setOpen(!popupCookie || (popupCookie && String(popupCookie).toLowerCase() !== String(campaignId).toLowerCase()));
  }, [campaignId, normalizedHeading]);

  const handleClose = () => {
    setOpen(false);
    setCookie(campaignId);
  };

  const variants = {
    initial: {
      x: '100%',
      opacity: 0,
    },
    active: {
      x: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        damping: 30,
        stiffness: 120,
        // delay: (animationCookie ? 0 : 7) + data.delay,
      },
    },
    exit: {
      x: '100%',
      opacity: 0,
      transition: {
        type: 'spring',
        damping: 30,
        stiffness: 120,
      },
    },
  };

  return (
    <ModalPortal>
      <AnimatePresence>
        {open && (
          <motion.div
            className="CornerPopup fixed z-50 bottom-2.5 right-3 w-[min(406px,calc(100%-20px))] py-6"
            {...props}
            aria-modal="true"
            role="dialog"
            variants={variants}
            initial="initial"
            animate="active"
            exit="exit"
          >
            <button className="absolute top-3 right-3 z-10" onClick={handleClose}>
              <Close role="presentation" aria-label="Close" className="fill-white" />
            </button>

            <ResponsiveImage
              image={data.backgroundImage}
              widths={{ xs: 406, sm: 406, md: 406, lg: 406, xl: 406, xxl: 406 }}
              heights={{ xs: 221, sm: 221, md: 221, lg: 221, xl: 221, xxl: 221 }}
              className="absolute z-0 inset-0 before:content-[''] before:absolute before:inset-0 before:bg-primary/60"
            />

            <div className="size-full flex relative flex-col items-center text-white gap-3 max-w-[334px] mx-auto">
              {campaignActive && data.campaignMode && (
                <IconRenderer selector={selectCampaignIcon} className="fill-current size-6" />
              )}
              {data.heading && data.heading.heading !== '' && (
                <HeadingTag data={data.heading} className="text-t4 -tracking-[0.03em] font-ivy-ora text-center" />
              )}
              {data.description && <p className="text-tiny-paragraph font-cera text-center">{data.description}</p>}
              {data.cta1 ? (
                campaignActive && data.campaignMode ? (
                  <FestiveButton link={data.cta1} />
                ) : (
                  <Button link={data.cta1} type="primary" theme="light" />
                )
              ) : null}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </ModalPortal>
  );
};

export default CornerPopup;
